import { z } from "zod";
import { defineStepConfig } from "@erp/workflows";

/**
 * Send Famous Shipment to Orderful 856 Step
 *
 * Fetches a Famous shipment by order number and sends it to Orderful as an 856 ASN
 * (Advanced Shipment Notice) EDI transaction.
 *
 * This step:
 * - Takes a Famous order number as input
 * - Fetches the shipment details from Famous Oracle database
 * - Validates the shipment data and customer information
 * - Builds and sends an 856 EDI message to Orderful
 * - Records the EDI transmission status
 *
 * Note: This is a backend-only step that requires Famous database access and
 * Orderful API integration.
 */
export const sendFamousShipmentToOrderful856Step = defineStepConfig({
  type: "send_famous_shipment_to_orderful_856",
  name: "Send Famous Shipment to Orderful 856",
  description:
    "Fetch a Famous shipment by order number and send it to Orderful as an 856 ASN EDI transaction",
  category: "Integration",

  inputSchema: z.object({
    famousOrderNumber: z
      .string()
      .min(1)
      .describe("The Famous order/shipment number to process"),
  }),

  outputSchema: z.object({
    orderfulTransactionId: z
      .string()
      .describe("The Orderful transaction ID for the 856 ASN"),
    famousShipmentId: z
      .string()
      .uuid()
      .describe("The internal database ID of the Famous shipment record"),
    purchaseOrderNumber: z
      .string()
      .optional()
      .describe("The customer's purchase order number from the shipment"),
  }),

  configSchema: z.object({
    stream: z
      .enum(["TEST", "LIVE"])
      .default("TEST")
      .describe("Orderful stream to send to (TEST or LIVE)"),
    skipIfAlreadySent: z
      .boolean()
      .default(true)
      .describe(
        "Skip sending if the 856 has already been sent for this shipment",
      ),
    fetchFromFamous: z
      .boolean()
      .default(true)
      .describe(
        "Whether to fetch fresh data from Famous (if false, uses existing cached data)",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
      maximumInterval: "2 minutes",
    },
  },
});
